// /campaigns/launch — create and fund a campaign as a step-by-step flow (one
// question per screen, like Instagram's boost flow), ending in a review and
// Stripe Checkout for the clipper pool (the 30% fee comes out of clipper payouts). ?edit=<id> reuses the
// same steps to edit a brief. Every answer is a tap: name and avatar come
// from the connected channel, and the title, pitch and overview are written
// from the picks. The one text box is pasting a VOD link.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCheck, FiChevronDown, FiChevronUp, FiDollarSign, FiEye, FiPieChart, FiPlus, FiTrash2, FiTrendingUp, FiUserPlus, FiX } from "react-icons/fi";
import { axiosInstance } from "../../config";
import {
  PLATFORM_FEE_RATE, PLATFORM_META, PLATFORM_POST_LABEL, POST_PLATFORMS,
  StreamImage, briefPath, fmtMoney, isDriveFolder,
} from "./campaignsShared";

const CATEGORIES = ["Just Chatting", "IRL", "Politics", "Gaming", "Entertainment", "Music", "Sports", "Education", "Lifestyle", "Technology", "Other"];
const DRAFT_KEY = "cb_campaign_launch_draft";
const MIN_POOL = 1000; // mirrors routes/campaigns.js
// Asked first, like an ad objective. Leads and sales campaigns also ask where
// clips send people (tracked with the Clipbait pixel) plus the comment
// trigger word and end card; views campaigns skip those.
const OBJECTIVES = [["views", "Views", FiEye], ["leads", "Leads", FiUserPlus], ["sales", "Sales", FiDollarSign]];
const POOL_ESTIMATE_CPM = 1; // only for the launch estimate; a real pool's rate depends on total views
const VIEW_CAMPAIGN_TYPES = [["cpm", "CPM", FiTrendingUp], ["pool", "Pool", FiPieChart]];
const OBJECTIVE_LABEL = Object.fromEntries(OBJECTIVES.map(([k, l]) => [k, l]));

// Tap-to-pick presets for the clip checklist (streamer clipping norms).
const STYLE_OPTIONS = [
  "Funny moments", "Reactions", "Rage & tilt", "Clutch plays", "Fails", "Big wins",
  "Hot takes", "Storytime", "Chat interactions", "Collabs & guests", "IRL moments", "Emotional moments",
];
// Rules aren't asked: every brief gets the same short standard set (the AI
// handles clip-level choices). An older brief keeps its own rules.
const standardDos = (name) => [`${name} is the main focus of every clip`, "Vertical 9:16 with captions"];
const STANDARD_DONTS = [
  "No botted or bought views", "No negative or misleading edits",
  "No logos or watermarks", "No reposting other clippers' clips",
];
const MIN_KEEP_UP_DAYS = 30; // mirrors routes/campaigns.js
const KEEP_UP_OPTIONS = [[30, "30 days"], [60, "60 days"], [90, "90 days"]];
const MAX_PAYOUT_OPTIONS = [["", "No cap"], ["100", "$100"], ["250", "$250"], ["500", "$500"], ["1000", "$1,000"]];
const MIN_PAYOUT_PRESETS = [5, 10, 25, 50, 100, 250, 500, 1000];
const PAY_UNIT_OPTIONS = [[1000, "1K views"], [10000, "10K views"], [100000, "100K views"], [1000000, "1M views"]];
const MIN_PAY_UNIT = 1000; // mirrors routes/campaigns.js
// What one pay unit is worth, e.g. $100 per 100,000 views.
const ratePerUnit = (f) => Math.round((Number(f.payoutPer1K) || 0) * (Number(f.payUnit) || 1000) / 10) / 100;
// The pay rate itself is the lowest a clip's minimum can go.
const minPayFor = (f) => Math.max(Number(f.minPayout) || 0, ratePerUnit(f));
const minPayOptions = (rate) => {
  const higher = MIN_PAYOUT_PRESETS.filter((x) => x > rate).slice(0, 3);
  return [...(rate > 0 ? [rate] : []), ...higher].map((x) => [String(x), fmtMoney(x, { cents: x % 1 !== 0 })]);
};
// Typed-in bubbles: tidy them up so a brief reads properly.
const cleanPhrase = (t) => [t.replace(/\s+/g, " ").trim()];
const cleanMoment = (t) => {
  const x = t.replace(/\s+/g, " ").trim().replace(/[.,;:!]+$/, "");
  return [x.charAt(0).toUpperCase() + x.slice(1)];
};
const cleanTags = (t) => t.split(/[\s,]+/).map((x) => x.toLowerCase().replace(/[^a-z0-9_]/g, "")).filter(Boolean).map((x) => `#${x}`);
// Add Streamer on the Me page comes back here when it's done.
const ADD_STREAMER_RETURN_KEY = "cb_add_streamer_return";
const POOL_DAY_OPTIONS = [[7, "1 week"], [14, "2 weeks"], [30, "1 month"], [60, "2 months"]]; // mirrors routes/campaigns.js
const DURATION_OPTIONS = [[0, "No end date"], [14, "2 weeks"], [30, "1 month"], [60, "2 months"], [90, "3 months"]];
const KEYWORD_OPTIONS = [["", "None"], ["LINK", "LINK"], ["LIVE", "LIVE"], ["CLIPS", "CLIPS"]];

// Options are built from the streamer's name so nobody has to type, though the
// bubbles can also be typed into. These are examples for clippers, not rules.
const setupOptions = (name) => {
  const base = String(name || "").toLowerCase().replace(/[^a-z0-9_.]+/g, "");
  return [["Clips", "clips"], ["Moments", "moments"], ["Highlights", "highlights"], ["Daily", "daily"]]
    .map(([word, slug]) => ({ displayName: `${name} ${word}`, username: `@${base}.${slug}` }));
};
const disclaimerOptions = (name) => [
  `Fan page, not ${name}'s official account`,
  `Unofficial ${name} clips`,
  `Fan-run clips of ${name}`,
];
const tagOptions = (name, category, platforms) => {
  const base = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const live = platforms.includes("youtube") ? "#youtubegaming" : "#twitch";
  return [...new Set([base && `#${base}`, base && `#${base}clips`, "#clips", "#fyp", "#streamer",
    `#${String(category || "gaming").toLowerCase().replace(/[^a-z0-9]+/g, "")}`, live, "#kick", "#viral"].filter(Boolean))];
};
const endDateFor = (days) => (days ? new Date(Date.now() + days * 864e5).toISOString().slice(0, 10) : "");
const daysUntil = (date) => (date ? Math.round((new Date(date) - Date.now()) / 864e5) : 0);

const EMPTY = {
  streamerName: "", brandName: "", title: "", description: "", category: "Gaming",
  brandId: "", nameExamples: "", bioExamples: "", setupSeeded: false,
  objective: "views", destinationUrl: "",
  overview: "", goals: "",
  displayName: "", username: "", bioDisclaimer: "", bioLink: "", setupNotes: "",
  platforms: ["tiktok", "instagram", "facebook"], usOnly: true, keepUpDays: 30, audienceNotes: "",
  styles: "", focus: "", hashtags: "", pinnedComment: "", manychatKeyword: "", dos: "", donts: "",
  pickedVods: [], pasteUrl: "", brandLogoUrl: "",
  payType: "cpm", poolDays: 30, payUnit: 1000,
  payoutPer1K: "1", minPayout: "", maxPayout: "", totalBudget: "1000", expiresAt: "",
  submitUrl: "", submitSteps: "",
  clippingGuidance: "",
};

const lines = (v) => String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);
const unlines = (a) => (a || []).join("\n");

function fromCampaign(c) {
  const b = c.brief || {};
  const ck = b.checklist || {};
  return {
    ...EMPTY,
    streamerName: c.streamerName || "", brandName: c.brandName || "", title: c.title || "",
    description: c.description || "", category: c.category || "Other",
    objective: c.objective || "views", destinationUrl: c.destinationUrl || "",
    overview: b.overview || "", goals: unlines(b.goals),
    displayName: b.pageSetup?.displayName || "", username: b.pageSetup?.username || "",
    bioDisclaimer: b.pageSetup?.bioDisclaimer || "", bioLink: b.pageSetup?.bioLink || "", setupNotes: b.pageSetup?.notes || "",
    platforms: c.platforms || [], usOnly: b.audience?.usOnly !== false, keepUpDays: Math.max(MIN_KEEP_UP_DAYS, b.audience?.keepUpDays ?? MIN_KEEP_UP_DAYS),
    audienceNotes: b.audience?.notes || "",
    styles: unlines(ck.styles), focus: unlines(ck.focus), hashtags: (ck.hashtags || []).join(" "),
    pinnedComment: ck.pinnedComment || "", manychatKeyword: ck.manychatKeyword || "",
    dos: unlines(ck.dos), donts: unlines(ck.donts),
    pickedVods: (c.sources || []).map((s) => ({ url: s.url, title: s.label || "", thumbnail: s.thumbnail || "" })),
    payType: c.payType || "cpm", poolDays: c.pool?.days || 30, payUnit: c.payUnit || 1000, brandId: c.brandId || "", setupSeeded: true,
    nameExamples: unlines(b.pageSetup?.nameExamples?.length ? b.pageSetup.nameExamples : [b.pageSetup?.displayName].filter(Boolean)),
    bioExamples: unlines(b.pageSetup?.bioExamples?.length ? b.pageSetup.bioExamples : [b.pageSetup?.bioDisclaimer].filter(Boolean)),
    payoutPer1K: String(c.payoutPer1K ?? ""), minPayout: c.pay?.minPayout ? String(c.pay.minPayout) : "",
    maxPayout: c.pay?.maxPayout ? String(c.pay.maxPayout) : "", totalBudget: String(c.totalBudget ?? ""),
    expiresAt: c.expiresAt ? String(c.expiresAt).slice(0, 10) : "",
    submitUrl: c.submitUrl || "", submitSteps: unlines(c.submitSteps),
    clippingGuidance: c.clippingGuidance || "",
  };
}

// Title, pitch and overview are written for the launcher from their picks.
const autoTitle = (f) => f.title || `${f.streamerName || f.brandName} Clipping`;
const autoDescription = (f) => f.description || (f.payType === "pool"
  ? `Clip ${f.streamerName || f.brandName}'s best moments and earn your share of the pool.`
  : `Clip ${f.streamerName || f.brandName}'s best moments and get paid per view.`);
const autoOverview = (f) => {
  if (f.overview) return f.overview;
  const name = f.streamerName || f.brandName;
  const goals = lines(f.goals).map((g) => g.toLowerCase());
  const styles = lines(f.styles).map((s) => s.toLowerCase());
  return [
    f.payType === "pool"
      ? `${name} is splitting a pool between clippers by views on their best moments.`
      : `${name} is paying clippers per view to post their best moments.`,
    goals.length ? `The goal: ${goals.join(", ")}.` : "",
    styles.length ? `Clips that win: ${styles.join(", ")}.` : "",
  ].filter(Boolean).join(" ");
};

function toPayload(f) {
  return {
    streamerName: f.streamerName,
    brandName: f.brandName || f.streamerName,
    title: autoTitle(f),
    objective: f.objective,
    destinationUrl: f.objective === "views" ? "" : f.destinationUrl.trim(),
    description: autoDescription(f),
    category: f.category,
    platforms: f.platforms,
    payType: f.payType,
    poolDays: f.poolDays,
    payUnit: f.payUnit,
    brandId: f.brandId || undefined,
    payoutPer1K: f.payoutPer1K,
    totalBudget: f.totalBudget,
    expiresAt: f.expiresAt || null,
    pay: { minPayout: minPayFor(f), maxPayout: f.maxPayout || 0 },
    submitUrl: f.submitUrl,
    submitSteps: lines(f.submitSteps),
    brief: {
      overview: autoOverview(f),
      goals: [OBJECTIVE_LABEL[f.objective] || "Views"],
      pageSetup: {
        displayName: lines(f.nameExamples)[0] || "", username: "", bioDisclaimer: lines(f.bioExamples)[0] || "",
        nameExamples: lines(f.nameExamples), bioExamples: lines(f.bioExamples),
        bioLink: f.bioLink === "none" ? "" : f.bioLink, notes: f.setupNotes,
      },
      audience: { usOnly: f.usOnly, keepUpDays: f.keepUpDays, notes: f.audienceNotes },
      checklist: {
        styles: lines(f.styles), focus: lines(f.focus),
        hashtags: f.hashtags.split(/[\s,]+/).filter(Boolean),
        pinnedComment: f.pinnedComment, manychatKeyword: f.manychatKeyword,
        dos: lines(f.dos).length ? lines(f.dos) : standardDos(f.streamerName || f.brandName),
        donts: lines(f.donts).length ? lines(f.donts) : STANDARD_DONTS,
      },
    },
    sources: (f.pickedVods || []).map((v) => ({ url: v.url, label: v.title, thumbnail: v.thumbnail })),
    clippingGuidance: f.clippingGuidance,
  };
}

// Bubbles you can tap on or off, or type your own. Taking off a typed one removes it.
function TagInput({ label, options, value, onChange, clean, placeholder }) {
  const [text, setText] = useState("");
  const picked = lines(value);
  const all = [...options, ...picked.filter((x) => !options.includes(x))];
  const add = () => {
    const added = clean(text).filter(Boolean);
    if (added.length) onChange(unlines([...new Set([...picked, ...added])]));
    setText("");
  };
  return (
    <div className="cmp-chipset">
      {label && <span className="cmp-input-label">{label}</span>}
      <div className="cmp-chipset-row">
        {all.map((x) => (
          <button type="button" key={x} className={`cmp-pick${picked.includes(x) ? " on" : ""}`}
            onClick={() => onChange(unlines(picked.includes(x) ? picked.filter((y) => y !== x) : [...picked, x]))}>
            {x}{picked.includes(x) && <FiX aria-hidden="true" />}
          </button>
        ))}
      </div>
      <div className="cmp-paste">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(); } }} />
        <button type="button" className="cmp-btn ghost" onClick={add}>Add</button>
      </div>
    </div>
  );
}

function Input({ label, hint, area, rows = 4, autoFocus, ...props }) {
  return (
    <label className="cmp-input">
      {label && <span className="cmp-input-label">{label}</span>}
      {area ? <textarea rows={rows} autoFocus={autoFocus} {...props} /> : <input autoFocus={autoFocus} {...props} />}
      {hint && <span className="cmp-input-hint">{hint}</span>}
    </label>
  );
}

function FilePick({ label, accept, file, existing, url, onFile, round }) {
  const preview = useMemo(() => (file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null), [file]);
  const img = preview || url || (existing && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(existing) ? existing : null);
  return (
    <label className={`cmp-file${round ? " round" : ""}`}>
      <span className="cmp-input-label">{label}</span>
      <div className="cmp-file-box">
        {img ? <img src={img} alt="" /> : <span>{file ? file.name : existing ? "Uploaded" : "+ Add"}</span>}
      </div>
      <input type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} />
    </label>
  );
}

// A big tappable row with a radio/check on the right (Instagram style).
function Choice({ title, on, onClick, icon, multi }) {
  return (
    <button type="button" className={`cmp-choice${on ? " on" : ""}`} onClick={onClick}>
      {icon && <span className="cmp-choice-icon">{icon}</span>}
      <span className="cmp-choice-text">
        <span className="cmp-choice-title">{title}</span>
      </span>
      <span className={`cmp-choice-mark${multi ? " multi" : ""}`}>{on && multi && <FiCheck />}</span>
    </button>
  );
}

// Multi-select chips (values = newline list). Items picked in an older brief
// that aren't presets still show so they can be unpicked.
function Pills({ label, options, value, onChange }) {
  return (
    <div className="cmp-chipset">
      {label && <span className="cmp-input-label">{label}</span>}
      <div className="cmp-chipset-row">
        {options.map(([v, text]) => (
          <button type="button" key={String(v)} className={`cmp-pick${String(value) === String(v) ? " on" : ""}`} onClick={() => onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

// Pills plus one box in the same row for typing your own amount. commit decides
// whether a half-typed value counts yet.
function PillsOrType({ label, options, value, onChange, prefix = "$", placeholder = "Type", commit = () => true }) {
  const [draft, setDraft] = useState(null);
  const isPreset = options.some(([v]) => String(v) === String(value));
  const shown = draft ?? (isPreset || !value ? "" : String(value));
  return (
    <div className="cmp-chipset">
      {label && <span className="cmp-input-label">{label}</span>}
      <div className="cmp-chipset-row">
        {options.map(([v, text]) => (
          <button type="button" key={String(v)} className={`cmp-pick${String(value) === String(v) ? " on" : ""}`} onClick={() => { setDraft(null); onChange(v); }}>
            {text}
          </button>
        ))}
        <label className={`cmp-pick cmp-pick-type${!isPreset && value ? " on" : ""}`}>
          {prefix && <span>{prefix}</span>}
          <input type="number" inputMode="decimal" min="0" value={shown} placeholder={placeholder} aria-label={label}
            onChange={(e) => { setDraft(e.target.value); if (commit(e.target.value)) onChange(e.target.value); }}
            onBlur={() => setDraft(null)} />
        </label>
      </div>
    </div>
  );
}

// A drag has to travel this far before the amount moves, so brushing or clicking
// the slider can't change money. Each pixel can be worth about $90 on the pool.
const SLIDER_DRAG_PX = 8;

function Slider({ label, prefix, value, onChange, min, max, step, suffix }) {
  const [draft, setDraft] = useState(null);
  const drag = useRef(null);
  const beforeTyping = useRef(null);
  const n = Number(value) || 0;
  const pct = Math.max(0, Math.min(100, ((Math.min(n, max) - min) / (max - min)) * 100));
  return (
    <div className="cmp-slider">
      <div className="cmp-slider-head">
        <span className="cmp-slider-label">{label}</span>
        <span className="cmp-slider-box">
          {prefix && <span>{prefix}</span>}
          <input type="number" inputMode="decimal" min="0" aria-label={label} value={draft ?? n}
            style={{ width: `${Math.max(2, String(draft ?? n).length) + 1}ch` }}
            onFocus={() => { beforeTyping.current = n; }}
            onWheel={(e) => e.target.blur()}
            onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }}
            onBlur={() => {
              // Left empty or at zero: put back what was there.
              if (draft !== null && !(Number(draft) > 0) && beforeTyping.current > 0) onChange(String(beforeTyping.current));
              setDraft(null);
            }} />
          {suffix && <span className="cmp-slider-suffix">{suffix}</span>}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={Math.min(n, max)}
        style={{ "--pct": `${pct}%` }}
        onPointerDown={(e) => { drag.current = { startX: e.clientX, x: e.clientX, moving: false }; }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          drag.current.x = e.clientX;
          if (Math.abs(e.clientX - drag.current.startX) >= SLIDER_DRAG_PX) drag.current.moving = true;
        }}
        onPointerUp={(e) => { drag.current = null; e.target.blur(); }}
        onPointerCancel={() => { drag.current = null; }}
        onChange={(e) => { if (!drag.current || drag.current.moving) onChange(e.target.value); }}
      />
    </div>
  );
}


export default function LaunchCampaignPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const [f, setF] = useState(() => {
    if (editId) return EMPTY;
    try {
      const d = { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") };
      return { ...d, totalBudget: String(Math.max(MIN_POOL, Number(d.totalBudget) || 0)), keepUpDays: Math.max(MIN_KEEP_UP_DAYS, Number(d.keepUpDays) || 0), payType: d.objective === "views" ? d.payType : "cpm" };
    } catch { return EMPTY; }
  });
  const [existing, setExisting] = useState(null);
  const [files, setFiles] = useState({ bannerImage: null, endCard: null });
  const [channel, setChannel] = useState(null);
  const [vodsOpen, setVodsOpen] = useState(false);
  const [testFunding, setTestFunding] = useState(false);
  const [myVods, setMyVods] = useState(null);
  const [savedStreamers, setSavedStreamers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const topRef = useRef(null);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  useEffect(() => {
    axiosInstance.get("/campaigns/funding/mode").then((r) => setTestFunding(!!r.data.testMode)).catch(() => {});
    axiosInstance.get("/users/me/brands").then((r) => {
      const list = r.data.brands || [];
      setSavedStreamers(list);
      if (editId) return;
      setF((prev) => {
        const still = list.find((b) => b._id === prev.brandId);
        const only = list.length === 1 ? list[0] : null;
        const use = still || only;
        return use
          ? { ...prev, brandId: use._id, streamerName: use.name, brandName: "" }
          : { ...prev, brandId: "", streamerName: "", brandName: "" };
      });
    }).catch(() => {});
    if (!editId) return;
    axiosInstance.get(`/campaigns/${editId}`)
      .then((r) => { setExisting(r.data); setF(fromCampaign(r.data)); })
      .catch(() => toast.error("Couldn't load that campaign"));
  }, [editId]);

  // The dropdown lists the picked streamer's streams, when there are any.
  useEffect(() => {
    if (!f.brandId) { setMyVods([]); return undefined; }
    let stale = false;
    setMyVods(null);
    axiosInstance.get("/campaigns/my-vods", { params: { brand: f.brandId } })
      .then((r) => {
        if (stale) return;
        setMyVods(r.data.vods || []);
        setChannel(r.data.channel || null);
      })
      .catch(() => { if (!stale) setMyVods([]); });
    return () => { stale = true; };
  }, [f.brandId]);

  // Keep a draft so leaving mid-flow doesn't lose the brief (images aren't kept).
  useEffect(() => {
    if (editId) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(f)); } catch { /* ignore */ }
  }, [f, editId]);

  const pool = Math.max(0, Number(f.totalBudget) || 0);
  const total = pool; // the 30% comes out of clipper payouts, not at checkout
  const pickStreamer = (b) => setF((p) => (p.brandId === b._id ? p : {
    ...p,
    brandId: b._id,
    streamerName: b.name,
    brandName: "",
    // Name-based suggestions belong to the previous streamer.
    nameExamples: "", bioExamples: "", bioLink: "", hashtags: "", setupSeeded: false,
    pickedVods: [],
  }));
  const cpm = Number(f.payoutPer1K) || 0;
  const payPool = f.payType === "pool";
  // A pool has no rate, so its estimate assumes one and says so.
  const estCpm = payPool ? POOL_ESTIMATE_CPM : cpm;
  const estViews = estCpm > 0 ? Math.round((pool / estCpm) * 1000) : 0;
  const estLabel = payPool ? `Estimated views at ${fmtMoney(POOL_ESTIMATE_CPM)} per 1K` : "Paid views";
  const rate = ratePerUnit(f);
  const unitScale = (Number(f.payUnit) || 1000) / 1000;
  const minPay = minPayFor(f);
  // Until it's paid, editing works like launching: same budget choices, ending in funding.
  const fundable = !editId || existing?.funding?.status === "unpaid";
  const canDelete = !!editId && !!existing && (existing.funding?.status !== "paid" || existing.funding?.testMode);
  const addStreamer = () => {
    try { sessionStorage.setItem(ADD_STREAMER_RETURN_KEY, "1"); } catch { /* ignore */ }
    navigate("/me?newBrand=true");
  };
  // Always the streamer's current photo and channel link, never a saved copy.
  const curStreamer = savedStreamers.find((b) => b._id === f.brandId);
  const streamerAvatar = curStreamer ? curStreamer.avatar : existing?.brandLogo;
  const streamerLink = (() => {
    const cs = curStreamer?.creatorSettings || {};
    const host = { twitch: "twitch.tv/", kick: "kick.com/", youtube: "youtube.com/@", rumble: "rumble.com/" }[cs.platform];
    return host && cs.channelUsername ? `https://${host}${cs.channelUsername}` : "";
  })();

  const togglePlatform = (p) => setF((prev) => ({
    ...prev,
    platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
  }));

  const name = f.streamerName || f.brandName;
  const wantsResults = f.objective !== "views";
  // Page setup starts on the first suggestion for the streamer's name.
  const setups = name ? setupOptions(name) : [];
  const disclaimers = name ? disclaimerOptions(name) : [];
  // Examples start switched on, and stay however the launcher leaves them.
  const suggestSetup = () => setF((prev) => (prev.setupSeeded ? prev : {
    ...prev,
    setupSeeded: true,
    nameExamples: unlines(setups.map((o) => o.displayName)),
    bioExamples: unlines(disclaimers),
    bioLink: prev.bioLink || streamerLink,
  }));
  const bioLink = f.bioLink || streamerLink;
  const setTags = (v) => setF((prev) => ({ ...prev, hashtags: lines(v).join(" ") }));
  const pinnedFor = (kw) => (kw ? `Comment ${kw} and I'll send you the link` : "");

  const isPicked = (url) => f.pickedVods.some((v) => v.url === url);
  const toggleVod = (v) => setF((prev) => ({
    ...prev,
    pickedVods: prev.pickedVods.some((x) => x.url === v.url)
      ? prev.pickedVods.filter((x) => x.url !== v.url)
      : [...prev.pickedVods, { url: v.url, title: v.title || "", thumbnail: v.thumbnail || "" }],
  }));
  // One link or many, separated by lines, spaces or commas.
  const addPasted = () => {
    const parts = f.pasteUrl.split(/[\s,]+/).filter(Boolean)
      .map((x) => (/^(www\.)?(drive|docs)\.google\.com\//i.test(x) ? `https://${x}` : x));
    const good = parts.filter((x) => /^https?:\/\/\S+\.\S+/i.test(x));
    if (!good.length) return toast.error("Paste a full link (https://…).");
    if (good.length < parts.length) toast.error(`Skipped ${parts.length - good.length} that didn't look like a link.`);
    setF((prev) => {
      const seen = new Set(prev.pickedVods.map((x) => x.url));
      const added = [...new Set(good)].filter((u) => !seen.has(u)).map((url) => {
        const yt = url.match(/(?:v=|youtu\.be\/|live\/|shorts\/)([\w-]{11})/);
        const title = isDriveFolder(url) ? "Google Drive folder" : /drive\.google\.com|docs\.google\.com/i.test(url) ? "Google Drive video" : "";
        return { url, title, thumbnail: yt ? `https://i.ytimg.com/vi/${yt[1]}/mqdefault.jpg` : "" };
      });
      return { ...prev, pasteUrl: "", pickedVods: [...prev.pickedVods, ...added] };
    });
    // Swap in the real names of Drive links as they come back.
    good.filter((u) => /drive\.google\.com|docs\.google\.com/i.test(u)).forEach((url) => {
      axiosInstance.get("/campaigns/drive-name", { params: { url } }).then((r) => {
        if (!r.data.name) return;
        setF((prev) => ({ ...prev, pickedVods: prev.pickedVods.map((v) => (v.url === url ? { ...v, title: r.data.name } : v)) }));
      }).catch(() => {});
    });
    return undefined;
  };

  // Each screen asks one thing, mostly by tapping. `check` returns an error or "".
  const runsForPills = (
    <Pills label="Runs for" options={DURATION_OPTIONS}
      value={DURATION_OPTIONS.some(([d]) => d === daysUntil(f.expiresAt)) ? daysUntil(f.expiresAt) : f.expiresAt ? "custom" : 0}
      onChange={(d) => setF((p) => ({ ...p, expiresAt: endDateFor(d) }))} />
  );
  const steps = [
    {
      key: "fund", nav: "Streamer", when: () => !editId,
      q: "Who do you want to fund?",
      check: () => (!f.brandId ? "Pick a streamer." : ""),
      body: (
        <div className="cmp-choices">
          {savedStreamers.map((b) => (
            <Choice
              key={b._id}
              title={b.name}
              on={f.brandId === b._id}
              onClick={() => pickStreamer(b)}
              icon={b.avatar
                ? <img className="cmp-choice-avatar" src={b.avatar} alt="" />
                : <span className="cmp-choice-avatar">{b.name?.[0]?.toUpperCase()}</span>}
            />
          ))}
          <button type="button" className="cmp-choice" onClick={addStreamer}>
            <span className="cmp-choice-icon"><span className="cmp-choice-avatar"><FiPlus size={22} /></span></span>
            <span className="cmp-choice-text"><span className="cmp-choice-title">Add a streamer</span></span>
          </button>
        </div>
      ),
    },
    {
      key: "objective", nav: "Objective",
      q: "What do you want from clippers?",
      check: () => "",
      body: (
        <>
          <div className="cmp-choices">
            {OBJECTIVES.map(([k, label, Icon]) => (
              <Choice key={k} icon={<Icon size={22} />} title={label} on={f.objective === k}
                onClick={() => setF((p) => ({ ...p, objective: k, payType: k === "views" ? p.payType : "cpm" }))} />
            ))}
          </div>
          {fundable && f.objective === "views" && (
            <>
              <span className="cmp-input-label">Campaign type</span>
              <div className="cmp-choices">
                {VIEW_CAMPAIGN_TYPES.map(([k, label, Icon]) => (
                  <Choice key={k} icon={<Icon size={22} />} title={label} on={f.payType === k} onClick={() => setF((p) => ({ ...p, payType: k }))} />
                ))}
              </div>
            </>
          )}
        </>
      ),
    },
    {
      key: "budget", nav: "Budget", when: () => fundable,
      q: "What's your budget?",
      check: () => (pool < MIN_POOL ? `The minimum clipper pool is ${fmtMoney(MIN_POOL)}.` : ""),
      summary: true,
      body: (
        <>
          <Slider label="Clipper pool" prefix="$" value={f.totalBudget} onChange={set("totalBudget")} min={MIN_POOL} max={Math.max(50000, pool)} step={100} />
          {payPool
            ? <Pills label="Pool runs for" options={POOL_DAY_OPTIONS} value={f.poolDays} onChange={set("poolDays")} />
            : runsForPills}
        </>
      ),
    },
    {
      key: "basics", nav: "Campaign",
      q: "Who are clippers clipping?",
      check: () => "",
      body: (
        <>
          <div className="cmp-choices">
            <Choice title={name} icon={streamerAvatar
              ? <img className="cmp-choice-avatar" src={streamerAvatar} alt="" />
              : <span className="cmp-choice-avatar">{name?.[0]?.toUpperCase()}</span>} on onClick={() => {}} />
          </div>
          <FilePick label="Banner" accept="image/png,image/jpeg,image/webp,image/gif" file={files.bannerImage} existing={existing?.bannerImage} onFile={(v) => setFiles((p) => ({ ...p, bannerImage: v }))} />
          <Pills label="Category" options={CATEGORIES.map((c) => [c, c])} value={f.category} onChange={set("category")} />
        </>
      ),
    },
    {
      key: "platforms", nav: "Platforms",
      q: "Where should clips be posted?",
      check: () => (!f.platforms.length ? "Pick at least one platform." : ""),
      body: (
        <>
          <div className="cmp-choices">
            {POST_PLATFORMS.map((p) => (
              <Choice key={p} multi icon={PLATFORM_META[p]?.icon(20)} title={PLATFORM_POST_LABEL[p]} on={f.platforms.includes(p)} onClick={() => togglePlatform(p)} />
            ))}
          </div>
          <Pills label="Which views count?" options={[[true, "US views only"], [false, "All views"]]} value={f.usOnly} onChange={(v) => setF((p) => ({ ...p, usOnly: v }))} />
          {!payPool && <Pills label="Keep posts up for" options={KEEP_UP_OPTIONS} value={f.keepUpDays} onChange={set("keepUpDays")} />}
        </>
      ),
    },
    {
      key: "setup", nav: "Page setup", optional: true,
      q: "What could clip pages be called?",
      check: () => "",
      body: (
        <>
          <TagInput label="Page name examples" options={setups.map((o) => o.displayName)} value={f.nameExamples}
            onChange={set("nameExamples")} clean={cleanPhrase} placeholder="Add a page name" />
          <TagInput label="Bio examples" options={disclaimers} value={f.bioExamples}
            onChange={set("bioExamples")} clean={cleanPhrase} placeholder="Add a bio" />
          {bioLink && (
            <Pills label="Bio link" options={[[bioLink, bioLink.replace(/^https?:\/\//, "")], ["none", "No link"]]}
              value={f.bioLink === "none" ? "none" : bioLink} onChange={set("bioLink")} />
          )}
        </>
      ),
    },
    {
      key: "checklist", nav: "Clip style", optional: true,
      q: "What moments should clippers post?",
      check: () => "",
      body: (
        <TagInput options={STYLE_OPTIONS} value={f.styles} onChange={set("styles")} clean={cleanMoment} placeholder="Add your own moment" />
      ),
    },
    {
      key: "hashtags", nav: "Hashtags", optional: true,
      q: "Which hashtags should clips use?",
      check: () => "",
      body: (
        <TagInput options={tagOptions(name, f.category, f.platforms)} value={unlines(f.hashtags.split(/[\s,]+/).filter(Boolean))}
          onChange={setTags} clean={cleanTags} placeholder="Add a hashtag" />
      ),
    },
    {
      key: "results", nav: OBJECTIVE_LABEL[f.objective], when: () => wantsResults,
      q: f.objective === "sales" ? "Where do clips send buyers?" : "Where do clips send leads?",
      check: () => (wantsResults && !/^https?:\/\/\S+\.\S+/i.test(f.destinationUrl.trim()) ? "Paste the link clips should send people to." : ""),
      body: (
        <>
          <Input autoFocus label="Paste your link" value={f.destinationUrl} onChange={set("destinationUrl")} placeholder="https://yourstore.com" />
          <Pills label="Comment trigger word" options={KEYWORD_OPTIONS} value={f.manychatKeyword}
            onChange={(v) => setF((p) => ({ ...p, manychatKeyword: v, pinnedComment: pinnedFor(v) }))} />
          <FilePick label="End card" accept="image/*,video/*" file={files.endCard} existing={existing?.brief?.endCard?.url} onFile={(v) => setFiles((p) => ({ ...p, endCard: v }))} />
        </>
      ),
    },
    {
      key: "sources", nav: "Streams", optional: true,
      q: "Which streams can clippers clip?",
      check: () => "",
      body: (
        <>
          {myVods === null && f.brandId && <div className="cmp-loading small">Loading streams…</div>}
          {myVods?.length > 0 && (
            <>
              <button type="button" className={`cmp-pick cmp-vod-toggle${vodsOpen ? " on" : ""}`} onClick={() => setVodsOpen((v) => !v)} aria-expanded={vodsOpen}>
                {channel?.name ? `${channel.name}'s streams` : "Streams"} ({myVods.length}) {vodsOpen ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
              </button>
              {vodsOpen && (
                <div className="cmp-choices">
                  {myVods.map((v) => (
                    <button type="button" key={v.id || v.url} className={`cmp-vod${isPicked(v.url) ? " on" : ""}`} onClick={() => toggleVod(v)}>
                      {v.thumbnail ? <img src={v.thumbnail} alt="" /> : <span className="cmp-vod-ph" />}
                      <span className="cmp-choice-text">
                        <span className="cmp-choice-title">{v.title}</span>
                      </span>
                      <span className="cmp-choice-mark multi">{isPicked(v.url) && <FiCheck />}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {f.pickedVods.map((v) => (
            <div key={v.url} className="cmp-vod on static">
              <StreamImage url={v.url} thumb={v.thumbnail} placeholderClass="cmp-vod-ph" />
              <span className="cmp-choice-text"><span className="cmp-choice-title">{v.title || v.url}</span></span>
              <button type="button" className="cmp-link" onClick={() => toggleVod(v)}>Remove</button>
            </div>
          ))}
          <div className="cmp-paste">
            <Input area rows={3} label="Paste links" value={f.pasteUrl} onChange={set("pasteUrl")} placeholder="Twitch, Kick, YouTube or Google Drive links, one or many. Drive folders work too." />
            <button type="button" className="cmp-btn ghost" onClick={addPasted}>Add</button>
          </div>
        </>
      ),
    },
    {
      key: "pay", nav: "Pay", when: () => !payPool,
      q: "What do you pay clippers?",
      check: () => (!(cpm > 0) ? "Set what you pay."
        : Number(f.maxPayout) > 0 && Number(f.maxPayout) < minPay ? `Max payout can't be under the ${fmtMoney(minPay, { cents: minPay % 1 !== 0 })} min payout.` : ""),
      summary: true,
      body: (
        <>
          <PillsOrType label="Pay per" options={PAY_UNIT_OPTIONS} value={f.payUnit} prefix="" placeholder="Views"
            commit={(v) => Number(v) >= MIN_PAY_UNIT}
            onChange={(v) => setF((p) => ({ ...p, payUnit: Number(v), minPayout: "" }))} />
          <Slider label={`Pay per ${Number(f.payUnit).toLocaleString()} views`} prefix="$" value={rate}
            onChange={(v) => setF((p) => ({ ...p, payoutPer1K: String((Number(v) * 1000) / Number(p.payUnit)), minPayout: Number(p.minPayout) >= Number(v) ? p.minPayout : "" }))}
            min={0.25 * unitScale} max={Math.max(10 * unitScale, rate)} step={0.25 * unitScale} />
          <PillsOrType label="Min payout per clip" options={minPayOptions(rate)} value={String(minPay)} onChange={set("minPayout")} />
          <PillsOrType label="Max payout per clip" options={MAX_PAYOUT_OPTIONS.filter(([v]) => !v || Number(v) >= minPay)} value={f.maxPayout} onChange={set("maxPayout")} />
          {!fundable && runsForPills}
        </>
      ),
    },
    { key: "review", nav: "Review", q: "Everything look good?", review: true },
  ].filter((x) => !x.when || x.when());

  const cur = steps[step];
  const last = steps.length - 1;
  const go = (i) => {
    if (steps[i]?.key === "setup" && !editId) suggestSetup();
    setStep(Math.max(0, Math.min(last, i)));
    topRef.current?.scrollIntoView({ block: "start" });
  };
  const next = () => {
    const err = cur.check?.();
    if (err) return toast.error(err);
    go(step + 1);
  };
  const back = () => (step === 0 ? navigate(editId ? -1 : "/campaigns/dashboard") : go(step - 1));

  const removeCampaign = async () => {
    if (!window.confirm(`Delete ${f.title || name || "this campaign"}? This can't be undone.`)) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/campaigns/${editId}`);
      toast.success("Campaign deleted");
      navigate("/campaigns/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't delete it. Try again.");
      setSaving(false);
    }
  };

  const save = async ({ fund }) => {
    const bad = steps.findIndex((s) => s.check && s.check());
    if (bad >= 0) { go(bad); return toast.error(steps[bad].check()); }
    const fd = new FormData();
    fd.append("data", JSON.stringify(toPayload(f)));
    Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
    setSaving(true);
    try {
      if (editId) {
        const r = await axiosInstance.put(`/campaigns/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (fund) {
          const pay = await axiosInstance.post(`/campaigns/${editId}/checkout`);
          window.location.href = pay.data.checkoutUrl; // Stripe Checkout
          return;
        }
        toast.success("Brief saved");
        navigate(briefPath(r.data));
      } else {
        const r = await axiosInstance.post("/campaigns/launch", fd, { headers: { "Content-Type": "multipart/form-data" } });
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        window.location.href = r.data.checkoutUrl; // Stripe Checkout
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Something went wrong. Try again.");
      setSaving(false);
    }
  };

  const submit = () => save({ fund: fundable });

  // Enter moves on from single-line inputs (textareas keep newlines).
  const onKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "file" && !cur.review) {
      e.preventDefault();
      next();
    }
  };

  const reviewRows = [
    ["basics", "Campaign", [name, f.category].filter(Boolean).join(" · ")],
    ["objective", "Objective", OBJECTIVE_LABEL[f.objective]],
    ["platforms", "Platforms & audience", `${f.platforms.map((p) => PLATFORM_POST_LABEL[p]).join(", ")} · ${f.usOnly ? "US views only" : "All views"}${payPool ? "" : ` · keep up ${f.keepUpDays} days`}`],
    ["setup", "Page name examples", lines(f.nameExamples).join(", ") || "None"],
    ["checklist", "Clip style", lines(f.styles).slice(0, 4).join(", ") || "Not set"],
    ["hashtags", "Hashtags", f.hashtags || "None"],
    ...(wantsResults ? [["results", OBJECTIVE_LABEL[f.objective], f.destinationUrl.replace(/^https?:\/\//, "") || "Not set"]] : []),
    ["sources", "Streams", `${f.pickedVods.length} picked${channel && !editId ? ` + latest from ${channel.name}` : ""}`],
    ...(fundable ? [["budget", "Budget", payPool ? `Share of ${fmtMoney(pool)} pool${!fundable ? "" : ` · ${POOL_DAY_OPTIONS.find(([d]) => d === Number(f.poolDays))?.[1] || ""}`}` : `${fmtMoney(pool)} clipper pool`]] : []),
    ...(!payPool ? [["pay", "Pay", `${fmtMoney(rate, { cents: rate % 1 !== 0 })} per ${Number(f.payUnit).toLocaleString()} views · min ${fmtMoney(minPay, { cents: minPay % 1 !== 0 })}${f.maxPayout ? ` · max ${fmtMoney(f.maxPayout)}/clip` : ""}`]] : []),
  ];

  return (
    <div className="cmp-flow" onKeyDown={onKeyDown}>
      <div ref={topRef} />
      <header className="cmp-flow-top">
        <button type="button" className="cmp-flow-back" onClick={back} aria-label={step === 0 ? "Cancel" : "Back"}>
          {step === 0 ? "Cancel" : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          )}
        </button>
        <div className="cmp-flow-title">{editId ? "Edit campaign" : cur.nav}</div>
        <div className="cmp-flow-count">{step + 1}/{steps.length}</div>
      </header>
      <div className="cmp-flow-progress" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1}>
        {steps.map((s, i) => (
          <button type="button" key={s.key} className={i <= step ? "on" : ""} onClick={() => i < step && go(i)} aria-label={s.nav} tabIndex={i < step ? 0 : -1} />
        ))}
      </div>

      <main className="cmp-flow-body" key={cur.key}>
        <h1 className="cmp-flow-q">{cur.q}</h1>

        {!cur.review && <div className="cmp-flow-fields">{cur.body}</div>}

        {cur.review && (
          <div className="cmp-review-preview">
            <div className="cmp-review-id">
              <div className="cmp-review-avatar">
                {streamerAvatar
                  ? <img src={streamerAvatar} alt="" />
                  : <span>{name?.[0]?.toUpperCase()}</span>}
              </div>
              <div className="cmp-review-names">
                <span>{name}</span>
                <strong>{autoTitle(f)}</strong>
              </div>
            </div>
          </div>
        )}

        {cur.review && (
          <div className="cmp-review">
            {reviewRows.map(([key, label, value]) => (
              <button type="button" key={key} className="cmp-review-row" onClick={() => go(steps.findIndex((s) => s.key === key))}>
                <span className="cmp-review-text">
                  <span className="cmp-review-label">{label}</span>
                  <span className="cmp-review-value">{value}</span>
                </span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            ))}
          </div>
        )}
      </main>

      <footer className="cmp-flow-foot">
        {!cur.summary && !cur.review && fundable && step > steps.findIndex((x) => x.key === "budget") && (
          <div className="cmp-flow-estimate">
            <span>{fmtMoney(pool)} budget</span>
            {estViews > 0 && <strong>{payPool ? "About" : "Up to"} {estViews.toLocaleString()} views</strong>}
          </div>
        )}
        {(cur.summary || cur.review) && fundable && (
          <div className="cmp-flow-summary">
            <div><span>Clipper pool</span><strong>{fmtMoney(pool)}</strong></div>
            {estViews > 0 && <div><span>{estLabel}</span><strong className="soft">{payPool ? "about" : "up to"} {estViews.toLocaleString()}</strong></div>}
            <div><span>Platform fee</span><strong className="soft">{Math.round(PLATFORM_FEE_RATE * 100)}% of clipper payouts</strong></div>
            {cur.review && <div className="total"><span>Total today</span><strong>{fmtMoney(total, { cents: total % 1 !== 0 })}</strong></div>}
          </div>
        )}
        {cur.review ? (
          <button type="button" className="cmp-btn big block" onClick={submit} disabled={saving}>
            {saving ? "Working…" : !fundable ? "Save brief" : `Fund & launch · ${fmtMoney(total, { cents: total % 1 !== 0 })}`}
          </button>
        ) : (
          <div className="cmp-flow-actions">
            {cur.optional && <button type="button" className="cmp-flow-skip" onClick={() => go(step + 1)}>Skip</button>}
            <button type="button" className="cmp-btn big block" onClick={next}>Next</button>
          </div>
        )}
        {cur.review && editId && (
          <div className="cmp-flow-edit-actions">
            {fundable && <button type="button" className="cmp-link" onClick={() => save({ fund: false })} disabled={saving}>Save without funding</button>}
            {canDelete && <button type="button" className="cmp-link" onClick={removeCampaign} disabled={saving}><FiTrash2 aria-hidden="true" /> Delete campaign</button>}
          </div>
        )}
        {cur.review && fundable && <p className="cmp-flow-fine">{testFunding
          ? <><strong className="cmp-hot">Test mode: no real money.</strong> Pay with card 4242 4242 4242 4242, any future date, any CVC.</>
          : "You'll pay on Stripe. The campaign goes live as soon as payment clears."}</p>}
      </footer>
    </div>
  );
}
